import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { getCreditsReferrals } from '../services/studentCreditsService';
import { fetchLearners } from '../services/studentAccountService';
import { uploadMaterialsForSessions } from '../services/bookingMaterialsService';
import {
  addBookingCartItems,
  updateBookingCartItem,
} from '../services/bookingCartService';
import BookSessionStickyHeader from './bookSession/BookSessionStickyHeader';
import BookSessionMaterialsStep from './bookSession/BookSessionMaterialsStep';
import BookSessionReviewStep from './bookSession/BookSessionReviewStep';
import BookSessionPricingSummary from './bookSession/BookSessionPricingSummary';
import BookSessionModals from './bookSession/BookSessionModals';
import {
  SERVICE_FEE,
  formatSessionTime,
  groupSessionsByLearner,
  groupSessionsBySubject,
  createEmptySessionMaterial,
  makeLocalFileEntry,
} from './bookSession/bookingHelpers';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const CheckIcon = (props) => (
  <svg {...props} className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const LockIcon = (props) => (
  <svg {...props} className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const ClockIcon = (props) => (
  <svg {...props} className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CreditCardIcon = (props) => (
  <svg {...props} className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? '00' : '30';
  const value = `${String(hours).padStart(2, '0')}:${minutes}`;
  const label = new Date(2000, 0, 1, hours, Number(minutes)).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return { value, label };
});

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const toLocalDateValue = (value) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const toTimeValue = (value) => {
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const combineDateAndTime = (date, time) => new Date(`${date}T${time}:00`);

const formatCalendarStartTime = (value) =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const BookSession = ({ tutorData, bookingData, onClose }) => {
  const navigate = useNavigate();
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [notes, setNotes] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('weekdays');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceWeekdays, setRecurrenceWeekdays] = useState([]);
  const [customRecurrenceDate, setCustomRecurrenceDate] = useState('');
  const [customRecurrenceDates, setCustomRecurrenceDates] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [sessionMaterials, setSessionMaterials] = useState({});
  const [activeLearnerTab, setActiveLearnerTab] = useState('');
  const subjectSectionRef = useRef(null);
  const [subjectCatalog, setSubjectCatalog] = useState([]);

  useEffect(() => {
    const cartItem = bookingData?.cartItem;
    if (!cartItem) return;
    const recurrence = cartItem.recurrenceData || {};
    const session = {
      id: cartItem.id,
      availabilityEventId: recurrence.availabilityEventId || `cart-${cartItem.id}`,
      availabilityStart: recurrence.availabilityStart || cartItem.startTime,
      availabilityEnd: recurrence.availabilityEnd || cartItem.endTime,
      tutorProfileId: cartItem.tutorProfileId,
      learnerLabel: cartItem.learnerLabel,
      subjectId: cartItem.subjectId,
      subjectName: cartItem.subjectName,
      notes: cartItem.notes || '',
      start: cartItem.startTime,
      end: cartItem.endTime,
      recurring: Boolean(recurrence.recurring),
      recurrencePattern: recurrence.recurrencePattern || null,
      recurrenceEndDate: recurrence.recurrenceEndDate || '',
      recurrenceWeekdays: recurrence.recurrenceWeekdays || [],
      customRecurrenceDates: recurrence.customRecurrenceDates || [],
      status: 'pending',
    };
    setSelectedSessions([session]);
    setEditingSessionId(cartItem.id);
    setSelectedEvent({
      id: session.availabilityEventId,
      title: formatSessionTime(session.start, session.end),
      start: session.availabilityStart,
      end: session.availabilityEnd,
      tutorProfileId: session.tutorProfileId,
    });
    setSelectedLearner(session.learnerLabel);
    setSelectedSubject(session.subjectId);
    setNotes(session.notes);
    setStartDate(toLocalDateValue(session.start));
    setStartTime(toTimeValue(session.start));
    setEndTime(toTimeValue(session.end));
    setIsRecurring(session.recurring);
    setRecurrencePattern(session.recurrencePattern || 'weekdays');
    setRecurrenceEndDate(session.recurrenceEndDate);
    setRecurrenceWeekdays(session.recurrenceWeekdays);
    setCustomRecurrenceDates(session.customRecurrenceDates);
  }, [bookingData?.cartItem]);

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValidation, setCodeValidation] = useState(null); // { valid: boolean, message: string, discount: number }
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  const [availableCredits, setAvailableCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(false);

  useEffect(() => {
    const loadCredits = async () => {
      try {
        const data = await getCreditsReferrals();
        const balance = Number(data?.availableCredits ?? 0);
        setAvailableCredits(balance);
        if (
          sessionStorage.getItem('applyAvailableCredits') === 'true' &&
          balance > 0
        ) {
          setUseCredits(true);
        }
      } catch {
        setAvailableCredits(0);
      }
    };
    loadCredits();
  }, []);

  useEffect(() => {
    const loadLearners = async () => {
      try {
        const response = await fetchLearners();
        const list = Array.isArray(response) ? response : response?.learners || [];
        setLearners(list);
        if (list.length > 0) {
          setSelectedLearner(list[0].learnerLabel);
          return;
        }
      } catch {
        setLearners([]);
      }

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const defaultLabel = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Self';
        setSelectedLearner(defaultLabel);
      } catch {
        setSelectedLearner('Self');
      }
    };

    loadLearners();
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequest('/api/v1/subjects')
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response)
          ? response
          : response?.subjects || response?.data || [];
        setSubjectCatalog(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSubjectCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subjectOptions = useMemo(() => {
    const names = bookingData?.subjects
      ? bookingData.subjects.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    const byName = new Map(
      subjectCatalog.map((s) => [String(s.name || '').toLowerCase(), s]),
    );
    return names.map((name) => ({
      name,
      id: byName.get(name.toLowerCase())?.id ?? null,
    }));
  }, [bookingData?.subjects, subjectCatalog]);

  // Fetch availability data and transform to calendar events
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!bookingData?.userId) return;

      try {
        setLoadingAvailability(true);
        let studentProfileId = '';
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          studentProfileId = user.id || user._id || user.userId || '';
        } catch {
          studentProfileId = '';
        }
        const params = new URLSearchParams();
        if (bookingData.tutorProfileId) params.set('tutorProspectId', bookingData.tutorProfileId);
        if (studentProfileId) params.set('studentId', studentProfileId);
        const availabilityResponse = await apiRequest(
          `/api/v1/availability/tutor/${bookingData.userId}/calendar-slots?${params.toString()}`,
          'GET',
        );
        const availabilityData = Array.isArray(availabilityResponse)
          ? availabilityResponse
          : availabilityResponse?.slots || [];
        
        // Transform availability data to calendar events
        const events = availabilityData
          .filter((slot) => {
            const status = slot.status || 'available';
            if (status === 'available') return true;
            return ['booked', 'pending'].includes(status) && slot.isOwnBooking;
          })
          .map(slot => ({
            id: `${slot.id}`,
            title: formatCalendarStartTime(slot.startTime),
            start: slot.startTime,
            end: slot.endTime,
            extendedProps: {
              availabilityId: slot.availabilityId || slot.id,
              tutorProfileId: bookingData.tutorProfileId,
              status: slot.status || 'available',
              isOwnBooking: Boolean(slot.isOwnBooking),
            }
          }));
        
        setCalendarEvents(events);
      } catch (error) {
        console.error('Error fetching availability:', error);
        setBookingError('Failed to load tutor availability');
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, [bookingData?.userId, bookingData?.tutorProfileId]);

  const getAvailableWindowEnd = (eventStart, eventEnd) => {
    const day = toLocalDateValue(eventStart);
    let windowEnd = new Date(eventEnd).getTime();
    let extended = true;
    while (extended) {
      extended = false;
      for (const event of calendarEvents) {
        const nextStart = new Date(event.start);
        const nextEnd = new Date(event.end);
        if (
          toLocalDateValue(nextStart) === day &&
          event.extendedProps?.status === 'available' &&
          nextStart.getTime() <= windowEnd &&
          nextEnd.getTime() > windowEnd
        ) {
          windowEnd = nextEnd.getTime();
          extended = true;
        }
      }
    }
    return new Date(windowEnd);
  };

  // Handle event click to select availability slot
  const handleEventClick = (info) => {
    const event = info.event;
    if (event.extendedProps.status !== 'available') {
      setBookingError(
        event.extendedProps.status === 'pending'
          ? 'This session time is pending and cannot be selected.'
          : 'This session time is already confirmed and cannot be selected.',
      );
      return;
    }
    const eventStart = new Date(event.start);
    const availableWindowEnd = getAvailableWindowEnd(eventStart, event.end);
    const defaultEnd = new Date(Math.min(eventStart.getTime() + 60 * 60 * 1000, availableWindowEnd.getTime()));
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      tutorProfileId: event.extendedProps.tutorProfileId
    });
    setStartDate(toLocalDateValue(eventStart));
    setStartTime(toTimeValue(eventStart));
    setEndTime(toTimeValue(defaultEnd));
    if (!editingSessionId) setRecurrenceWeekdays([eventStart.getDay()]);
    setBookingError(null);
  };

  const availableStartTimes = selectedEvent
    ? TIME_OPTIONS.filter(({ value }) => {
        const option = combineDateAndTime(startDate, value);
        return option >= new Date(selectedEvent.start) && option < new Date(selectedEvent.end);
      })
    : [];

  const availableEndTimes = selectedEvent && startTime
    ? TIME_OPTIONS.filter(({ value }) => {
        const start = combineDateAndTime(startDate, startTime);
        const end = combineDateAndTime(startDate, value);
        const availableWindowEnd = getAvailableWindowEnd(selectedEvent.start, selectedEvent.end);
        const maxEnd = Math.min(start.getTime() + 8 * 60 * 60 * 1000, availableWindowEnd.getTime());
        return end.getTime() >= start.getTime() + 30 * 60 * 1000 && end.getTime() <= maxEnd;
      })
    : [];

  const getRecurrenceDates = () => {
    if (!isRecurring) return [startDate];
    if (recurrencePattern === 'custom') {
      return Array.from(new Set([startDate, ...customRecurrenceDates])).sort();
    }
    if (!recurrenceEndDate || recurrenceEndDate <= startDate) return [startDate];

    if (recurrencePattern === 'weekdays') {
      const dates = [startDate];
      const cursor = combineDateAndTime(startDate, '12:00');
      const recurrenceEnd = combineDateAndTime(recurrenceEndDate, '12:00');
      while (dates.length < 100) {
        cursor.setDate(cursor.getDate() + 1);
        if (cursor > recurrenceEnd) break;
        if (recurrenceWeekdays.includes(cursor.getDay())) dates.push(toLocalDateValue(cursor));
      }
      return dates;
    }

    const dates = [startDate];
    const cursor = combineDateAndTime(startDate, '12:00');
    const recurrenceEnd = combineDateAndTime(recurrenceEndDate, '12:00');
    while (dates.length < 100) {
      if (recurrencePattern === 'daily') cursor.setDate(cursor.getDate() + 1);
      if (recurrencePattern === 'monthly') cursor.setMonth(cursor.getMonth() + 1);
      if (cursor > recurrenceEnd) break;
      dates.push(toLocalDateValue(cursor));
    }
    return dates;
  };

  const validateSessionForm = () => {
    if (!selectedEvent || !startDate || !startTime || !endTime) {
      return { error: 'Please select an available date and time', occurrences: [] };
    }
    if (!selectedLearner) {
      return { error: 'Please select a learner', occurrences: [] };
    }
    if (!selectedSubject) {
      return { error: 'Please select a subject', occurrences: [] };
    }

    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(startDate, endTime);
    const duration = end.getTime() - start.getTime();
    if (duration < 30 * 60 * 1000 || duration > 8 * 60 * 60 * 1000) {
      return {
        error: 'Session duration must be between 30 minutes and 8 hours',
        occurrences: [],
      };
    }

    const dates = editingSessionId ? [startDate] : getRecurrenceDates();
    if (isRecurring && recurrencePattern === 'weekdays' && recurrenceWeekdays.length === 0) {
      return { error: 'Please select at least one recurring weekday', occurrences: [] };
    }
    if (isRecurring && !editingSessionId && dates.length < 2) {
      return { error: 'Please select at least one additional recurring date', occurrences: [] };
    }

    const occurrences = dates.map((date) => {
      const occurrenceStart = combineDateAndTime(date, startTime);
      const occurrenceEnd = combineDateAndTime(date, endTime);
      const availability = calendarEvents.find((event) =>
        event.extendedProps?.status === 'available' &&
        new Date(event.start) <= occurrenceStart &&
        new Date(event.end) > occurrenceStart,
      );
      if (!availability || getAvailableWindowEnd(availability.start, availability.end) < occurrenceEnd) {
        return null;
      }
      return { date, availability };
    });
    const unavailableIndex = occurrences.findIndex((occurrence) => !occurrence);
    if (unavailableIndex >= 0) {
      return {
        error: `The tutor is not available for the selected time on ${new Date(`${dates[unavailableIndex]}T12:00:00`).toLocaleDateString()}.`,
        occurrences: [],
      };
    }

    return { error: null, occurrences };
  };

  const sessionFormValidation = validateSessionForm();
  const canSaveSession = !sessionFormValidation.error;

  const buildSessionDrafts = () => {
    const { error, occurrences } = validateSessionForm();
    if (error) {
      setBookingError(error);
      return null;
    }

    return occurrences.map(({ date, availability }, index) => ({
      id: editingSessionId || `${Date.now()}-${index}`,
      availabilityEventId: availability.id,
      availabilityStart: availability.start,
      availabilityEnd: availability.end,
      tutorProfileId: selectedEvent.tutorProfileId,
      learnerLabel: selectedLearner,
      subjectId: selectedSubject,
      subjectName: subjectOptions.find((opt) => opt.id === selectedSubject)?.name,
      notes,
      start: combineDateAndTime(date, startTime).toISOString(),
      end: combineDateAndTime(date, endTime).toISOString(),
      recurring: isRecurring,
      recurrencePattern: isRecurring ? recurrencePattern : null,
      recurrenceEndDate: isRecurring ? recurrenceEndDate : '',
      recurrenceWeekdays: isRecurring ? recurrenceWeekdays : [],
      customRecurrenceDates: isRecurring ? customRecurrenceDates : [],
      status: 'pending',
    }));
  };

  const resetSessionForm = () => {
    setSelectedEvent(null);
    setStartDate('');
    setStartTime('');
    setEndTime('');
    setNotes('');
    setIsRecurring(false);
    setRecurrencePattern('weekdays');
    setRecurrenceEndDate('');
    setRecurrenceWeekdays([]);
    setCustomRecurrenceDate('');
    setCustomRecurrenceDates([]);
    setEditingSessionId(null);
  };

  const handleSaveSession = () => {
    const drafts = buildSessionDrafts();
    if (!drafts) return;
    if (editingSessionId) {
      setPendingAction({ type: 'edit', session: drafts[0] });
      return;
    }
    setSelectedSessions((current) => [...current, ...drafts].sort(
      (a, b) => new Date(a.start) - new Date(b.start),
    ));
    resetSessionForm();
    setBookingError(null);
  };

  const handleEditSession = (session) => {
    setEditingSessionId(session.id);
    setSelectedEvent({
      id: session.availabilityEventId,
      title: formatSessionTime(session.start, session.end),
      start: session.availabilityStart,
      end: session.availabilityEnd,
      tutorProfileId: session.tutorProfileId,
    });
    setSelectedLearner(session.learnerLabel);
    setSelectedSubject(session.subjectId);
    setNotes(session.notes || '');
    setStartDate(toLocalDateValue(session.start));
    setStartTime(toTimeValue(session.start));
    setEndTime(toTimeValue(session.end));
    setIsRecurring(session.recurring);
    setRecurrencePattern(session.recurrencePattern || 'weekdays');
    setRecurrenceEndDate(session.recurrenceEndDate || '');
    setRecurrenceWeekdays(session.recurrenceWeekdays || [new Date(session.start).getDay()]);
    setCustomRecurrenceDates(session.customRecurrenceDates || []);
    setBookingError(null);
  };

  const confirmPendingAction = () => {
    if (pendingAction?.type === 'delete') {
      setSelectedSessions((current) => current.filter((session) => session.id !== pendingAction.session.id));
      setSessionMaterials((current) => {
        const next = { ...current };
        delete next[pendingAction.session.id];
        return next;
      });
      if (editingSessionId === pendingAction.session.id) resetSessionForm();
    }
    if (pendingAction?.type === 'edit') {
      setSelectedSessions((current) => current
        .map((session) => session.id === editingSessionId ? pendingAction.session : session)
        .sort((a, b) => new Date(a.start) - new Date(b.start)));
      resetSessionForm();
    }
    setPendingAction(null);
    setBookingError(null);
  };

  const sessionsGroupedBySubject = useMemo(
    () => groupSessionsBySubject(selectedSessions, tutorData.rate),
    [selectedSessions, tutorData.rate],
  );

  const sessionsByLearner = useMemo(
    () => groupSessionsByLearner(selectedSessions),
    [selectedSessions],
  );

  const isSubjectExpanded = (subjectName, index) => {
    if (expandedSubjects[subjectName] !== undefined) return expandedSubjects[subjectName];
    return index === 0;
  };

  const toggleSubjectExpanded = (subjectName, index) => {
    setExpandedSubjects((prev) => {
      const current = prev[subjectName] !== undefined ? prev[subjectName] : index === 0;
      return { ...prev, [subjectName]: !current };
    });
  };

  const handleGoToMaterials = () => {
    if (!selectedSessions.length) {
      setBookingError('Please add at least one session');
      return;
    }
    setActiveLearnerTab(sessionsByLearner[0]?.label || '');
    setBookingError(null);
    setBookingStep(2);
  };

  const handleGoToReview = () => {
    setBookingError(null);
    setBookingStep(3);
  };

  const handleEditSubjectSessions = (subjectId) => {
    if (subjectId) setSelectedSubject(subjectId);
    setBookingStep(1);
    subjectSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelAllConfirm = () => {
    setSelectedSessions([]);
    setSessionMaterials({});
    setExpandedSubjects({});
    setActiveLearnerTab('');
    setSelectedSubject(null);
    resetSessionForm();
    setBookingStep(1);
    setShowCancelAllModal(false);
    setBookingError(null);
    subjectSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getUserEmail = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.email || 'your email';
    } catch {
      return 'your email';
    }
  };

  const handleAddFiles = (sessionId, fileList) => {
    if (!fileList?.length) return;
    const additions = Array.from(fileList).map(makeLocalFileEntry);
    setSessionMaterials((prev) => ({
      ...prev,
      [sessionId]: {
        ...createEmptySessionMaterial(),
        ...prev[sessionId],
        files: [...(prev[sessionId]?.files || []), ...additions],
      },
    }));
  };

  const handleRemoveFile = (sessionId, fileId) => {
    setSessionMaterials((prev) => ({
      ...prev,
      [sessionId]: {
        ...createEmptySessionMaterial(),
        ...prev[sessionId],
        files: (prev[sessionId]?.files || []).filter((file) => file.id !== fileId),
      },
    }));
  };

  const handleCommentsChange = (sessionId, comments) => {
    setSessionMaterials((prev) => ({
      ...prev,
      [sessionId]: { ...createEmptySessionMaterial(), ...prev[sessionId], comments },
    }));
  };

  const handleApplyToLearner = (sourceSession) => {
    const source = sessionMaterials[sourceSession.id];
    if (!source?.files?.length) return;

    setSessionMaterials((prev) => {
      const next = { ...prev };
      selectedSessions
        .filter(
          (session) =>
            session.learnerLabel === sourceSession.learnerLabel && session.id !== sourceSession.id,
        )
        .forEach((session) => {
          next[session.id] = {
            ...createEmptySessionMaterial(),
            ...next[session.id],
            files: source.files.map((file) => ({
              ...file,
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            })),
          };
        });
      return next;
    });
  };

  // Validate discount code
  const handleValidateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setCodeValidation({ valid: false, message: 'Please enter a discount code' });
      return;
    }

    const serviceFee = SERVICE_FEE;
    const totalAmount = bookingTotal || Number(tutorData.rate) + serviceFee;

    setValidatingCode(true);
    setCodeValidation(null);

    try {
      const response = await apiRequest(
        `/api/v1/promo-codes/validate?code=${encodeURIComponent(discountCode.trim())}&amount=${totalAmount}`,
        'GET'
      );

      if (response.valid) {
        setCodeValidation({
          valid: true,
          message: 'Promo code applied successfully!',
          discount: response.discount || 0
        });
        setAppliedDiscount(response.discount || 0);
      } else {
        setCodeValidation({
          valid: false,
          message: response.message || 'Invalid promo code'
        });
        setAppliedDiscount(0);
      }
    } catch (err) {
      console.error('Failed to validate promo code:', err);
      setCodeValidation({
        valid: false,
        message: err.message || 'Failed to validate promo code'
      });
      setAppliedDiscount(0);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleConfirmBooking = async () => {
    if (selectedSessions.length === 0) {
      setBookingError('Please add at least one session');
      return;
    }

    setIsBooking(true);
    setBookingError(null);

    try {
      // Get student profile ID from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setBookingError('User not authenticated. Please log in again.');
        setIsBooking(false);
        return;
      }
      
      const user = JSON.parse(userStr);
      const studentProfileId = user.id || user._id || user.userId;
      
      if (!studentProfileId) {
        setBookingError('Unable to identify user. Please log in again.');
        setIsBooking(false);
        return;
      }

      const serviceFee = SERVICE_FEE;
      const totalPrice = selectedSessions.reduce((total, session) => {
        const hours = (new Date(session.end) - new Date(session.start)) / (60 * 60 * 1000);
        return total + Number(tutorData.rate) * hours + serviceFee;
      }, 0);

      const creditToApply = useCredits
        ? Math.min(availableCredits, Math.max(totalPrice - (codeValidation?.valid ? appliedDiscount : 0), 0))
        : 0;
      if (creditToApply > 0) {
        sessionStorage.setItem('pendingCreditApplication', String(creditToApply));
      }

      const uploadedMaterials = await uploadMaterialsForSessions(selectedSessions, sessionMaterials);

      for (const session of selectedSessions) {
        const hours = (new Date(session.end) - new Date(session.start)) / (60 * 60 * 1000);
        const materialEntry = sessionMaterials[session.id];
        const noteParts = [session.notes, materialEntry?.comments].filter(Boolean);
        const payload = {
          studentProfileId: parseInt(studentProfileId, 10),
          tutorProfileId: session.tutorProfileId,
          subjectId: session.subjectId,
          startTime: session.start,
          endTime: session.end,
          totalPrice: Number(tutorData.rate) * hours + serviceFee,
          notes: noteParts.length ? noteParts.join('\n\n') : undefined,
          learnerLabel: session.learnerLabel || undefined,
          discountCode: codeValidation?.valid ? discountCode.trim() : undefined,
          materials: uploadedMaterials[session.id],
        };
        await apiRequest('/api/v1/bookings', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      sessionStorage.removeItem('applyAvailableCredits');
      // Show success modal
      setShowConfirmationModal(true);
    } catch (error) {
      console.error('Booking error:', error);
      setBookingError(error.message || 'Failed to book session. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  const handleAddToCart = async () => {
    if (selectedSessions.length === 0) {
      setBookingError('Please add at least one session');
      return;
    }
    setIsBooking(true);
    setBookingError(null);
    try {
      const payloads = selectedSessions.map((session) => ({
        learnerLabel: session.learnerLabel,
        tutorProfileId: session.tutorProfileId,
        subjectId: session.subjectId || undefined,
        subjectName: session.subjectName,
        startTime: session.start,
        endTime: session.end,
        totalPrice:
          Number(tutorData.rate) *
            ((new Date(session.end) - new Date(session.start)) / (60 * 60 * 1000)) +
          SERVICE_FEE,
        notes: session.notes || undefined,
        recurrenceData: {
          availabilityEventId: session.availabilityEventId,
          availabilityStart: session.availabilityStart,
          availabilityEnd: session.availabilityEnd,
          recurring: session.recurring,
          recurrencePattern: session.recurrencePattern,
          recurrenceEndDate: session.recurrenceEndDate,
          recurrenceWeekdays: session.recurrenceWeekdays,
          customRecurrenceDates: session.customRecurrenceDates,
        },
      }));
      if (bookingData?.cartItem?.id && payloads.length === 1) {
        await updateBookingCartItem(bookingData.cartItem.id, payloads[0]);
      } else {
        await addBookingCartItems(payloads);
      }
      navigate('/on-demand-learning/cart');
    } catch (error) {
      setBookingError(error.message || 'Unable to add sessions to the cart');
    } finally {
      setIsBooking(false);
    }
  };

  // Auto-dismiss modal after user inactivity (optional fallback)
  useEffect(() => {
    if (showConfirmationModal) {
      const timer = setTimeout(() => {
        setShowConfirmationModal(false);
        onClose();
      }, 30000);

      return () => clearTimeout(timer);
    }
  }, [showConfirmationModal, onClose]);



  const subjectDisplayLine =
    bookingData?.subjectDisplayLine && String(bookingData.subjectDisplayLine).trim()
      ? bookingData.subjectDisplayLine
      : '';

  const selectedLearningHours = selectedSessions.reduce(
    (total, session) => total + (new Date(session.end) - new Date(session.start)) / (60 * 60 * 1000),
    0,
  );
  const sessionsSubtotal = Number(tutorData.rate) * selectedLearningHours;
  const serviceFees = selectedSessions.length * SERVICE_FEE;
  const bookingTotal = sessionsSubtotal + serviceFees;
  const creditsToUse = useCredits
    ? Math.min(availableCredits, Math.max(bookingTotal - appliedDiscount, 0))
    : 0;

  const { datesWithAvailability, datesWithAnyEvent } = useMemo(() => {
    const availability = new Set();
    const any = new Set();
    calendarEvents.forEach((event) => {
      const dateKey = toLocalDateValue(event.start);
      any.add(dateKey);
      if (event.extendedProps?.status === 'available') availability.add(dateKey);
    });
    return { datesWithAvailability: availability, datesWithAnyEvent: any };
  }, [calendarEvents]);

  return (
    <div className="min-h-[calc(100vh-4.25rem)] bg-[#f5f7fa]">
      <BookSessionModals
        showConfirmationModal={showConfirmationModal}
        showCancelAllModal={showCancelAllModal}
        tutorName={tutorData.name}
        userEmail={getUserEmail()}
        pendingAction={pendingAction}
        onCloseConfirmation={() => {
          setShowConfirmationModal(false);
          onClose();
        }}
        onCancelAllExit={() => setShowCancelAllModal(false)}
        onCancelAllConfirm={handleCancelAllConfirm}
        onPendingExit={() => setPendingAction(null)}
        onPendingConfirm={confirmPendingAction}
      />

      <BookSessionStickyHeader
        tutorName={tutorData.name}
        profilePhotoUrl={tutorData.profilePhotoUrl}
        subjectDisplayLine={subjectDisplayLine}
        currentStep={showConfirmationModal ? 4 : bookingStep}
        onBack={onClose}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-2">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left Column - Session Details */}
            <div className="lg:col-span-2 space-y-6">

              {bookingStep === 2 ? (
                <BookSessionMaterialsStep
                  sessionsByLearner={sessionsByLearner}
                  activeLearner={activeLearnerTab}
                  onLearnerChange={setActiveLearnerTab}
                  sessionMaterials={sessionMaterials}
                  onAddFiles={handleAddFiles}
                  onRemoveFile={handleRemoveFile}
                  onCommentsChange={handleCommentsChange}
                  onApplyToLearner={handleApplyToLearner}
                  onSaveContinue={handleGoToReview}
                  onSkip={handleGoToReview}
                />
              ) : bookingStep === 3 ? (
                <BookSessionReviewStep
                  sessionsGroupedBySubject={sessionsGroupedBySubject}
                  isSubjectExpanded={isSubjectExpanded}
                  onToggleSubject={toggleSubjectExpanded}
                  onEditSubject={handleEditSubjectSessions}
                  tutorRate={tutorData.rate}
                  bookingError={bookingError}
                />
              ) : (
              <>

              {/* Loading State */}
              {loadingAvailability && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-medium-blue"></div>
                    <span className="ml-3 text-gray-600">Loading availability...</span>
                  </div>
                </div>
              )}

              {/* Calendar Section */}
              {!loadingAvailability && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Available Time Slot</h3>
                  <p className="text-sm text-gray-500 mb-4">Click on a blue time slot to select it for booking</p>
                  <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'var(--brand-medium-blue)' }} />
                      Available
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: 'var(--brand-gold)' }} />
                      Selected
                    </span>
                    <span className="flex items-center gap-1.5">
                      <ClockIcon className="h-3 w-3 text-amber-600" />
                      <span className="h-3 w-3 rounded-sm bg-amber-500" />
                      Pending
                    </span>
                    <span className="flex items-center gap-1.5">
                      <LockIcon className="h-3 w-3 text-[#55634b]" />
                      <span className="h-3 w-3 rounded-sm bg-[#6b7c5f]" />
                      Booked
                    </span>
                    <span className="flex items-center gap-1.5">
                      <LockIcon className="h-3 w-3 text-gray-400" />
                      <span
                        className="h-3 w-3 rounded-sm border border-gray-300 bg-gray-200"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(135deg, rgba(156,163,175,0.55) 0, rgba(156,163,175,0.55) 2px, transparent 2px, transparent 4px)',
                        }}
                      />
                      Unavailable
                    </span>
                  </div>
                  <div className="book-session-calendar">
                    <FullCalendar
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                      }}
                      slotMinTime="00:00:00"
                      slotMaxTime="24:00:00"
                      slotDuration="00:30:00"
                      selectable={false}
                      selectMirror={false}
                      dayMaxEvents={true}
                      weekends={true}
                      allDaySlot={false}
                      height="auto"
                      displayEventTime={false}
                      dayCellClassNames={(arg) => {
                        if (arg.view.type !== 'dayGridMonth') return [];
                        const dateKey = toLocalDateValue(arg.date);
                        if (datesWithAvailability.has(dateKey)) return ['book-session-day--available'];
                        if (!datesWithAnyEvent.has(dateKey)) return ['book-session-day--unavailable'];
                        return [];
                      }}
                      dayCellContent={(arg) => {
                        if (arg.view.type !== 'dayGridMonth') return arg.dayNumberText;
                        const dateKey = toLocalDateValue(arg.date);
                        const isUnavailable = !datesWithAvailability.has(dateKey) && !datesWithAnyEvent.has(dateKey);
                        if (!isUnavailable) return arg.dayNumberText;
                        return (
                          <span className="flex w-full items-center justify-between">
                            <span>{arg.dayNumberText}</span>
                            <LockIcon className="h-3 w-3 text-gray-400" />
                          </span>
                        );
                      }}
                      eventContent={(eventInfo) => {
                        const selected = selectedEvent?.id === eventInfo.event.id;
                        const status = eventInfo.event.extendedProps.status;
                        return (
                          <div
                            className="flex items-center justify-between gap-1 overflow-hidden px-1 font-medium"
                            title={status === 'pending' ? 'Pending approval' : status === 'booked' ? 'Booked' : undefined}
                          >
                            <span className="truncate">{eventInfo.event.title}</span>
                            {selected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-white" />}
                            {status === 'booked' && <LockIcon className="h-3.5 w-3.5 flex-shrink-0" />}
                            {status === 'pending' && <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />}
                          </div>
                        );
                      }}
                      events={calendarEvents}
                      eventClick={handleEventClick}
                      eventClassNames={(info) => {
                        const selected = selectedEvent?.id === info.event.id;
                        const status = info.event.extendedProps.status;
                        if (selected) {
                          return ['book-session-calendar-event--selected', 'cursor-pointer'];
                        }
                        if (status === 'booked') {
                          return ['book-session-calendar-event--booked', 'cursor-not-allowed'];
                        }
                        if (status === 'pending') {
                          return ['book-session-calendar-event--pending', 'cursor-not-allowed'];
                        }
                        return ['book-session-calendar-event--available', 'cursor-pointer'];
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingSessionId ? 'Edit Session' : 'Add Session'}
                  </h3>
                  {editingSessionId && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                      Editing selection
                    </span>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Select Learner</span>
                    <select
                      value={selectedLearner}
                      onChange={(e) => setSelectedLearner(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-medium-blue focus:outline-none focus:ring-2 focus:ring-brand-medium-blue/30"
                    >
                      {learners.length === 0 && selectedLearner && (
                        <option value={selectedLearner}>{selectedLearner}</option>
                      )}
                      {learners.map((learner) => (
                        <option key={learner.learnerLabel} value={learner.learnerLabel}>
                          {learner.learnerFirstName || learner.learnerLabel}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="sm:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Start Date</span>
                    <input
                      type="date"
                      value={startDate}
                      readOnly
                      aria-readonly="true"
                      className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700"
                    />
                    <span className="mt-1 block text-xs text-gray-500">Choose a different date from the calendar.</span>
                  </label>

                  <label>
                    <span className="mb-1 block text-sm font-medium text-gray-700">Start Time</span>
                    <select
                      value={startTime}
                      onChange={(e) => {
                        const nextStart = e.target.value;
                        setStartTime(nextStart);
                        const start = combineDateAndTime(startDate, nextStart);
                        const availableWindowEnd = getAvailableWindowEnd(selectedEvent.start, selectedEvent.end);
                        setEndTime(toTimeValue(new Date(Math.min(start.getTime() + 60 * 60 * 1000, availableWindowEnd.getTime()))));
                      }}
                      disabled={!selectedEvent}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                    >
                      <option value="">Select time</option>
                      {availableStartTimes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>

                  <label>
                    <span className="mb-1 block text-sm font-medium text-gray-700">End Time</span>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={!startTime}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                    >
                      <option value="">Select time</option>
                      {availableEndTimes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                </div>

                <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-medium-blue focus:ring-brand-medium-blue"
                  />
                  Create recurring sessions
                </label>

                {isRecurring && (
                  <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <label>
                        <span className="mb-1 block text-sm font-medium text-gray-700">Repeats</span>
                        <select
                          value={recurrencePattern}
                          onChange={(e) => setRecurrencePattern(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekdays">Weekly on selected days</option>
                          <option value="monthly">Monthly</option>
                          <option value="custom">Custom dates</option>
                        </select>
                      </label>
                      {recurrencePattern !== 'custom' && (
                        <label>
                          <span className="mb-1 block text-sm font-medium text-gray-700">End Date</span>
                          <input
                            type="date"
                            min={startDate}
                            value={recurrenceEndDate}
                            onChange={(e) => setRecurrenceEndDate(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                          />
                        </label>
                      )}
                    </div>
                    {recurrencePattern === 'weekdays' && (
                      <div className="mt-4">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Recurring Weekdays</span>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {WEEKDAY_OPTIONS.map((day) => (
                            <label
                              key={day.value}
                              className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={recurrenceWeekdays.includes(day.value)}
                                onChange={() => setRecurrenceWeekdays((days) =>
                                  days.includes(day.value)
                                    ? days.filter((value) => value !== day.value)
                                    : [...days, day.value].sort(),
                                )}
                                className="h-4 w-4 rounded border-gray-300 text-brand-medium-blue focus:ring-brand-medium-blue"
                              />
                              {day.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {recurrencePattern === 'custom' && (
                      <div className="mt-4">
                        <div className="flex gap-2">
                          <input
                            type="date"
                            min={startDate}
                            value={customRecurrenceDate}
                            onChange={(e) => setCustomRecurrenceDate(e.target.value)}
                            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (!customRecurrenceDate || customRecurrenceDate === startDate) return;
                              setCustomRecurrenceDates((dates) => Array.from(new Set([...dates, customRecurrenceDate])).sort());
                              setCustomRecurrenceDate('');
                            }}
                            className="rounded-lg bg-brand-medium-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark-blue"
                          >
                            Add date
                          </button>
                        </div>
                        {customRecurrenceDates.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {customRecurrenceDates.map((date) => (
                              <button
                                type="button"
                                key={date}
                                onClick={() => setCustomRecurrenceDates((dates) => dates.filter((item) => item !== date))}
                                className="rounded-full bg-white px-3 py-1 text-xs text-gray-700 shadow-sm hover:bg-red-50 hover:text-red-600"
                                title="Remove date"
                              >
                                {new Date(`${date}T12:00:00`).toLocaleDateString()} ×
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Empty state when no availability */}
              {!loadingAvailability && calendarEvents.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">This tutor has no available time slots at the moment.</p>
                </div>
              )}

              {/* Select Subject Section */}
              {subjectOptions.length > 0 && (
                <div ref={subjectSectionRef} className="rounded-xl border border-gray-200 bg-white p-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">Choose a Subject</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {subjectOptions.map((option) => (
                      <button
                        key={option.name}
                        type="button"
                        onClick={() => option.id && setSelectedSubject(option.id)}
                        disabled={!option.id}
                        title={!option.id ? 'This subject is not in the catalog yet' : undefined}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${selectedSubject === option.id
                          ? 'bg-brand-medium-blue text-white border-brand-gold shadow-lg'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-brand-light-blue'
                          } ${!option.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes (Optional)</h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., I need help with calculus"
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="4"
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleSaveSession}
                  disabled={!canSaveSession}
                  title={canSaveSession ? undefined : sessionFormValidation.error}
                  className="flex-1 rounded-lg bg-brand-medium-blue px-6 py-3 font-semibold text-white shadow-lg transition enabled:hover:bg-brand-dark-blue disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none"
                >
                  {editingSessionId ? 'Update Session' : isRecurring ? 'Add Recurring Sessions' : 'Add Session'}
                </button>
                {(selectedEvent || editingSessionId) && (
                  <button
                    type="button"
                    onClick={resetSessionForm}
                    className="rounded-lg border border-gray-300 px-6 py-3 font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {availableCredits > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Credits</h3>
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={useCredits}
                      onChange={(e) => setUseCredits(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-medium-blue focus:ring-brand-medium-blue"
                    />
                    <span className="text-sm text-gray-700">
                      Apply up to{' '}
                      <strong>${Math.min(availableCredits, Math.max(bookingTotal - appliedDiscount, 0)).toFixed(2)}</strong>{' '}
                      from your balance ({availableCredits.toFixed(2)} available) at checkout.
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    <a href="/credits-referrals" className="text-brand-medium-blue hover:underline">
                      Manage credits
                    </a>
                  </p>
                </div>
              )}

              {/* Discount Code Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount Code (Optional)</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setCodeValidation(null);
                    }}
                    placeholder="Enter promo code"
                    className="flex-1 border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                  />
                  <button
                    onClick={handleValidateDiscountCode}
                    disabled={validatingCode || !discountCode.trim()}
                    className="bg-brand-medium-blue hover:bg-brand-dark-blue text-white px-6 py-3 rounded-lg text-sm font-medium transition-colors shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {validatingCode ? 'Validating...' : 'Apply'}
                  </button>
                </div>
                
                {/* Validation Message */}
                {codeValidation && (
                  <div className={`mt-3 p-3 rounded-lg ${codeValidation.valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className={`text-sm ${codeValidation.valid ? 'text-green-800' : 'text-yellow-800'}`}>
                      {codeValidation.message}
                    </p>
                    {codeValidation.valid && codeValidation.discount > 0 && (
                      <p className="text-sm text-green-700 font-medium mt-1">
                        You save ${codeValidation.discount.toFixed(2)}!
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Error Message */}
              {bookingError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{bookingError}</p>
                </div>
              )}

              {/* Payment Method Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CreditCardIcon className="text-amber-500" />
                    <div>
                      <h4 className="font-medium text-gray-900">Credit / Debit Card</h4>
                      <p className="text-sm text-gray-500">Visa, Mastercard, Amex</p>
                    </div>
                  </div>
                </div>
              </div>
              </>
              )}
            </div>

            {/* Right Column - Booking Summary */}
            <div className="space-y-6">
              {bookingStep === 1 && (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">Selected Sessions</h3>
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-brand-medium-blue">
                    {selectedSessions.length}
                  </span>
                </div>
                {selectedSessions.length === 0 ? (
                  <p className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                    Select a calendar time, complete the Add Session form, and add it for review.
                  </p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {selectedSessions.map((session) => (
                      <div
                        key={session.id}
                        className={`rounded-lg border p-3 transition ${editingSessionId === session.id ? 'border-brand-gold bg-amber-50' : 'border-gray-200 hover:border-brand-medium-blue hover:bg-blue-50/40'}`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditSession(session)}
                            className="min-w-0 flex-1 text-left"
                            aria-label={`Edit session on ${new Date(session.start).toLocaleDateString()}`}
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-brand-dark-blue">{session.learnerLabel}</span>
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">Pending</span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-gray-800">
                              {new Date(session.start).toLocaleDateString('en-US', {
                                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                              })}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatSessionTime(session.start, session.end)}
                              {session.recurring && <span className="font-semibold text-amber-700"> (Recurring)</span>}
                            </p>
                            <p className="mt-1 text-xs font-medium text-brand-medium-blue">{session.subjectName}</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingAction({ type: 'delete', session })}
                            className="rounded-full bg-red-50 p-1.5 text-red-600 hover:bg-red-600 hover:text-white"
                            aria-label="Delete session"
                            title="Delete session"
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              <BookSessionPricingSummary
                bookingStep={bookingStep}
                selectedSessions={selectedSessions}
                sessionsByLearner={sessionsByLearner}
                sessionMaterials={sessionMaterials}
                sessionsSubtotal={sessionsSubtotal}
                serviceFees={serviceFees}
                appliedDiscount={appliedDiscount}
                useCredits={useCredits}
                availableCredits={availableCredits}
                creditsToUse={creditsToUse}
                bookingTotal={bookingTotal}
                selectedLearningHours={selectedLearningHours}
                isBooking={isBooking}
                bookingError={bookingError}
                onPayNow={handleConfirmBooking}
                onAddToCart={handleAddToCart}
                onCancelAll={() => setShowCancelAllModal(true)}
                onReviewBooking={handleGoToMaterials}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookSession;
